/**
 * Markdown Template Viewer Component
 * Simplified version - displays markdown template generated from RFP structure
 */
import React, { useState, useMemo } from 'react';
import { Card, Button } from '@tracekit/core';
import { Download, Copy, Eye, Edit } from 'lucide-react';
import type { RfpStructureMetadata } from '../../types/rfp';

export interface MarkdownTemplateViewerProps {
  structure?: RfpStructureMetadata;
  initialMarkdown?: string;
  onSave?: (markdown: string) => void;
  loading?: boolean;
  editable?: boolean;
}

export function MarkdownTemplateViewer({
  structure,
  initialMarkdown,
  onSave,
  loading,
  editable = false,
}: MarkdownTemplateViewerProps) {
  const [mode, setMode] = useState<'preview' | 'edit'>('preview');
  const [markdown, setMarkdown] = useState(initialMarkdown || '');

  // Generate markdown from structure
  const generatedMarkdown = useMemo(() => {
    if (initialMarkdown) return initialMarkdown;
    if (!structure) return '';

    const lines: string[] = [];
    
    // Header
    lines.push('# RFP Response Template');
    lines.push('');
    lines.push(`*Generated: ${new Date().toISOString().split('T')[0]}*`);
    lines.push('');
    lines.push('---');
    lines.push('');

    // Sections
    if (structure.sections && structure.sections.length > 0) {
      structure.sections.forEach((section: any) => {
        const headingLevel = '#'.repeat(Math.min((section.level || 2) + 1, 6));
        const sectionName = section.name || section.heading || section.title || 'Unnamed Section';
        lines.push(`${headingLevel} ${sectionName}`);
        lines.push('');

        // Add content preview if available
        if (section.content_preview && section.content_preview.length > 10) {
          lines.push('**RFP Content:**');
          lines.push('');
          lines.push('> ' + section.content_preview.substring(0, 300));
          if (section.content_preview.length > 300) {
            lines.push('> ...');
          }
          lines.push('');
        }

        // Add response placeholder
        lines.push('**Response:**');
        lines.push('');
        lines.push('*[TODO: Add your response here]*');
        lines.push('');

        // Add requirements that match this section (by section name/text match)
        const sectionReqs = (structure.requirements || []).filter(
          (req: any) => {
            const reqSection = (req.section || '').toLowerCase();
            const secName = sectionName.toLowerCase();
            return reqSection.includes(secName) || secName.includes(reqSection);
          }
        );
        if (sectionReqs.length > 0) {
          lines.push('**Requirements:**');
          lines.push('');
          sectionReqs.forEach((req: any) => {
            const badge = req.type === 'mandatory' ? '🔴' : req.type === 'technical' ? '🔵' : '⚪';
            lines.push(`- ${badge} **${(req.type || 'requirement').toUpperCase()}**: ${req.text}`);
          });
          lines.push('');
        }

        // Add evaluation criteria that match this section
        const sectionCriteria = (structure.evaluation_criteria || []).filter(
          (crit: any) => {
            const critSection = (crit.section || '').toLowerCase();
            const secName = sectionName.toLowerCase();
            return critSection.includes(secName) || secName.includes(critSection);
          }
        );
        if (sectionCriteria.length > 0) {
          lines.push('**Evaluation Criteria:**');
          lines.push('');
          sectionCriteria.forEach((crit: any) => {
            lines.push(`- ⭐ **${crit.text}**`);
          });
          lines.push('');
        }

        lines.push('---');
        lines.push('');
      });
    }

    // Summary Statistics
    lines.push('## Summary');
    lines.push('');
    lines.push('### Extraction Statistics');
    lines.push('');
    lines.push(`- **Total Sections**: ${structure.sections?.length || 0}`);
    lines.push(`- **Total Requirements**: ${structure.requirements?.length || 0}`);
    lines.push(`- **Evaluation Criteria**: ${structure.evaluation_criteria?.length || 0}`);
    lines.push('');

    return lines.join('\n');
  }, [structure, initialMarkdown]);

  React.useEffect(() => {
    if (generatedMarkdown) {
      setMarkdown(generatedMarkdown);
    }
  }, [generatedMarkdown]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      alert('Copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy:', err);
      alert('Failed to copy to clipboard');
    }
  };

  const handleDownload = () => {
    const blob = new Blob([markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rfp-response-template-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-pulse">Generating markdown template...</div>
      </div>
    );
  }

  if (!structure && !initialMarkdown) {
    return (
      <div className="p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
        <p className="text-blue-800 dark:text-blue-200">
          No RFP structure available. Please extract structure first or provide markdown content.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">Markdown Template</h3>
          {structure && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {structure.sections?.length || 0} sections • {structure.requirements?.length || 0} requirements
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setMode(mode === 'preview' ? 'edit' : 'preview')}
            variant="outline"
            size="sm"
          >
            {mode === 'preview' ? <Edit className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button onClick={handleCopy} variant="outline" size="sm">
          <Copy className="h-4 w-4 mr-2" />
          Copy
        </Button>
        <Button onClick={handleDownload} variant="outline" size="sm">
          <Download className="h-4 w-4 mr-2" />
          Download
        </Button>
      </div>

      {/* Content */}
      <Card className="p-4">
        {mode === 'edit' ? (
          <textarea
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            className="w-full h-96 p-4 font-mono text-sm border rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            placeholder="Markdown content..."
            disabled={!editable}
          />
        ) : (
          <div className="prose dark:prose-invert max-w-none">
            <pre className="whitespace-pre-wrap font-mono text-sm bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-auto max-h-96">
              {markdown}
            </pre>
          </div>
        )}
      </Card>

      {editable && onSave && mode === 'edit' && (
        <div className="flex justify-end">
          <Button onClick={() => onSave(markdown)}>
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
}
